import { AssignmentRow } from "@/components/Assignments/Table/AssignmentRow";
import { ClientRow } from "@/components/Clients/Table/ClientRow";
import { EmployeeRow } from "@/components/Employee/Table/EmployeeRow";
import { InvoiceRow } from "@/components/Invoice/Table/InvoiceRow";
import { PointOfContactRow } from "@/components/PointOfContacts/Table/PointOfContactRow";
import { ProjectRow } from "@/components/Projects/Table/ProjectRow";
import { RFIRow } from "@/components/RFIs/Table/RFIRow";
import { SubmittalRow } from "@/components/Submittals/Table/SubmittalRow";
import { TaskRow } from "@/components/Tasks/Table/TaskRow";
import { AssignmentProps } from "@/interfaces/assignment_types";
import { ClientProps } from "@/interfaces/client_types";
import { EmployeeProps } from "@/interfaces/employee_type";
import { InvoiceProps } from "@/interfaces/invoices_types";
import { PointOfContactProps } from "@/interfaces/point-of-contact_types";
import { ProjectProps } from "@/interfaces/project_types";
import { RFIProps } from "@/interfaces/rfi_types";
import { SubmittalProps } from "@/interfaces/submittal_types";
import { TaskProps } from "@/interfaces/tasks_types";
import {
	ProjectModes,
	RFIModes,
	SubmittalModes,
	TaskModes,
} from "../Interfaces/FactoryProps";
import { GenericTableClass } from "./GenericTableClass";
import {
	AssignmentTable,
	CASProjectTable,
	ClientProjectTable,
	ClientTable,
	EmployeeTable,
	FinanceProjectTable,
	InvoiceTable,
	MiniCASProjectTable,
	MiniProjectTable,
	PointOfContactTable,
	ProjectTable,
	RFITable,
	SubmittalTable,
	TaskTable,
	YourProjectsTable,
	YourRFIsTable,
	YourSubmittalsTable,
	YourTasksTable,
} from "./ModelTables";

/**
 * ### Factory class for creating tables
 *
 * Creates tables based on the model name
 * The data and columns are defined the Options folder.
 */
export class GenericTableFactory {
	static createProjectTable(
		mode: ProjectModes,
	): GenericTableClass<ProjectProps> {
		switch (mode) {
			case "personal":
				return new YourProjectsTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: true,
				});
			case "all":
				return new ProjectTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: true,
				});
			case "mini":
				return new MiniProjectTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: false,
					show_filters: false,
				});
			case "client":
				return new ClientProjectTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: false,
				});
			case "finance":
				return new FinanceProjectTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: false,
				});
			case "cas":
				return new CASProjectTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: true,
				});
			case "mini-cas":
				return new MiniCASProjectTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: false,
					show_filters: false,
				});
			default:
				console.error("Invalid mode for project table, showing all projects");
				return new ProjectTable({
					expandableRowComponent: (props) => <ProjectRow data={props.data} />,
					model_name: "project",
					show_arrow: true,
				});
		}
	}

	static createTaskTable(mode: TaskModes): GenericTableClass<TaskProps> {
		switch (mode) {
			case "personal":
				return new YourTasksTable({
					expandableRowComponent: (props) => <TaskRow data={props.data} />,
					model_name: "task",
				});
			case "all":
				return new TaskTable({
					expandableRowComponent: (props) => <TaskRow data={props.data} />,
					model_name: "task",
				});
			default:
				console.error("Invalid mode for task table, showing all tasks");
				return new TaskTable({
					expandableRowComponent: (props) => <TaskRow data={props.data} />,
					model_name: "task",
				});
		}
	}

	static createSubmittalTable(
		mode: SubmittalModes,
	): GenericTableClass<SubmittalProps> {
		switch (mode) {
			case "personal":
				return new YourSubmittalsTable({
					expandableRowComponent: (props) => <SubmittalRow data={props.data} />,
					model_name: "submittal",
				});
			case "all":
				return new SubmittalTable({
					expandableRowComponent: (props) => <SubmittalRow data={props.data} />,
					model_name: "submittal",
				});
			default:
				console.error(
					"Invalid mode for submittal table, showing all submittals",
				);
				return new SubmittalTable({
					expandableRowComponent: (props) => <SubmittalRow data={props.data} />,
					model_name: "submittal",
				});
		}
	}

	static createRFITable(mode: RFIModes): GenericTableClass<RFIProps> {
		switch (mode) {
			case "personal":
				return new YourRFIsTable({
					expandableRowComponent: (props) => <RFIRow data={props.data} />,
					model_name: "rfi",
				});
			case "all":
				return new RFITable({
					expandableRowComponent: (props) => <RFIRow data={props.data} />,
					model_name: "rfi",
				});
			default:
				console.error("Invalid mode for RFI table, showing all RFIs");
				return new RFITable({
					expandableRowComponent: (props) => <RFIRow data={props.data} />,
					model_name: "rfi",
				});
		}
	}

	static createAssignmentTable(): GenericTableClass<AssignmentProps> {
		return new AssignmentTable({
			expandableRowComponent: (props) => <AssignmentRow data={props.data} />,
			model_name: "assignment",
		});
	}

	static createInvoiceTable(): GenericTableClass<InvoiceProps> {
		return new InvoiceTable({
			expandableRowComponent: (props) => <InvoiceRow data={props.data} />,
			model_name: "invoice",
		});
	}

	static createEmployeeTable(): GenericTableClass<EmployeeProps> {
		return new EmployeeTable({
			expandableRowComponent: (props) => <EmployeeRow data={props.data} />,
			model_name: "user",
		});
	}

	static createClientTable(): GenericTableClass<ClientProps> {
		return new ClientTable({
			expandableRowComponent: (props) => <ClientRow data={props.data} />,
			model_name: "client",
		});
	}

	static createPointOfContactTable(): GenericTableClass<PointOfContactProps> {
		return new PointOfContactTable({
			expandableRowComponent: (props) => (
				<PointOfContactRow data={props.data} />
			),
			model_name: "pointofcontact",
		});
	}
}
